import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Home, ChefHat, Shirt, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const bhkTypes = [
  { id: '1bhk', label: '1 BHK', rooms: 3 },
  { id: '2bhk', label: '2 BHK', rooms: 5 },
  { id: '3bhk', label: '3 BHK', rooms: 7 },
  { id: '4bhk', label: '4 BHK', rooms: 9 },
  { id: '5bhk+', label: '5 BHK+', rooms: 11 },
];

const roomOptions = [
  { id: 'living', label: 'Living Room', icon: Home },
  { id: 'master-bedroom', label: 'Master Bedroom', icon: Home },
  { id: 'bedroom-2', label: 'Bedroom 2', icon: Home },
  { id: 'bedroom-3', label: 'Bedroom 3', icon: Home },
  { id: 'kitchen', label: 'Kitchen', icon: ChefHat },
  { id: 'wardrobe', label: 'Wardrobe', icon: Shirt },
  { id: 'study', label: 'Study Room', icon: Home },
  { id: 'pooja', label: 'Pooja Room', icon: Home },
];

const packages = [
  {
    id: 'essential',
    name: 'Essential',
    description: 'Quality basics for smart spaces',
    pricePerSqFt: 1200,
    features: ['Quality laminates', 'Standard hardware', 'Basic lighting'],
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Refined materials, elevated finish',
    pricePerSqFt: 1800,
    features: ['Premium veneers', 'Soft-close hardware', 'Designer lighting'],
  },
  {
    id: 'luxe',
    name: 'Luxe',
    description: 'Bespoke craftsmanship at its finest',
    pricePerSqFt: 2500,
    features: ['Custom finishes', 'Italian hardware', 'Curated fixtures'],
  },
];

export const EstimateCalculator = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedBHK, setSelectedBHK] = useState(null);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(null);

  const calculateEstimate = () => {
    if (!selectedBHK || !selectedPackage) return 0;
    
    const bhk = bhkTypes.find(b => b.id === selectedBHK);
    const pkg = packages.find(p => p.id === selectedPackage);
    
    // Base area calculation based on BHK
    const baseArea = {
      '1bhk': 450,
      '2bhk': 700,
      '3bhk': 1000,
      '4bhk': 1400,
      '5bhk+': 1800,
    }[selectedBHK] || 700;
    
    // Room multiplier
    const roomMultiplier = selectedRooms.length / (bhk?.rooms || 5);
    
    return Math.round(baseArea * pkg.pricePerSqFt * Math.max(roomMultiplier, 0.5));
  };

  const handleNext = () => {
    if (currentStep === 1 && !selectedBHK) {
      toast.error('Please select your BHK type');
      return;
    }
    if (currentStep === 2 && selectedRooms.length === 0) {
      toast.error('Please select at least one room');
      return;
    }
    if (currentStep === 3 && !selectedPackage) {
      toast.error('Please select a package');
      return;
    }
    
    if (currentStep === 3) {
      setEstimatedPrice(calculateEstimate());
    }
    
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const toggleRoom = (roomId) => {
    setSelectedRooms(prev => 
      prev.includes(roomId) 
        ? prev.filter(r => r !== roomId)
        : [...prev, roomId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/estimate`, {
        bhk_type: selectedBHK,
        rooms: selectedRooms,
        package: selectedPackage,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        estimated_price: estimatedPrice,
      });

      toast.success('Thank you! We\'ll contact you shortly with a detailed estimate.');
      
      // Reset form
      setCurrentStep(1);
      setSelectedBHK(null);
      setSelectedRooms([]);
      setSelectedPackage(null);
      setFormData({ name: '', phone: '', email: '' });
      setEstimatedPrice(null);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      ref={ref}
      data-testid="estimate-calculator-section"
      className="section-padding bg-[#F5F0E8]"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="overline text-[#B5703A] block mb-4">Estimate</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2A2A2A] tracking-tight mb-4">
            Plan Your Interiors
          </h2>
          <p className="text-[#3D3D3D] font-light max-w-lg mx-auto">
            Get a quick estimate tailored to your space.
          </p>
        </motion.div>

        {/* Step Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center mb-10"
        >
          <div className="step-indicator">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`step-dot ${currentStep >= step ? 'active' : ''}`}
                data-testid={`step-dot-${step}`}
              />
            ))}
          </div>
        </motion.div>

        {/* Calculator Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="bg-[#D9CFC0] p-8 lg:p-12"
        >
          <AnimatePresence mode="wait">
            {/* Step 1: BHK Selection */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-8">
                  <span className="text-sm text-[#3D3D3D]">Step 1 of 4</span>
                  <h3 className="text-2xl font-light text-[#2A2A2A] mt-2">Select your BHK type</h3>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {bhkTypes.map((bhk) => (
                    <button
                      key={bhk.id}
                      onClick={() => setSelectedBHK(bhk.id)}
                      className={`p-4 border transition-all duration-300 ${
                        selectedBHK === bhk.id
                          ? 'bg-[#B5703A] text-white border-[#B5703A]'
                          : 'bg-[#F5F0E8] text-[#2A2A2A] border-[#C4B6A4] hover:border-[#B5703A]'
                      }`}
                      data-testid={`bhk-option-${bhk.id}`}
                    >
                      <span className="block font-medium">{bhk.label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: Room Selection */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-8">
                  <span className="text-sm text-[#3D3D3D]">Step 2 of 4</span>
                  <h3 className="text-2xl font-light text-[#2A2A2A] mt-2">Select rooms to design</h3>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {roomOptions.map((room) => {
                    const Icon = room.icon;
                    return (
                      <button
                        key={room.id}
                        onClick={() => toggleRoom(room.id)}
                        className={`p-4 border transition-all duration-300 ${
                          selectedRooms.includes(room.id)
                            ? 'bg-[#B5703A] text-white border-[#B5703A]'
                            : 'bg-[#F5F0E8] text-[#2A2A2A] border-[#C4B6A4] hover:border-[#B5703A]'
                        }`}
                        data-testid={`room-option-${room.id}`}
                      >
                        <Icon size={20} className="mx-auto mb-2" />
                        <span className="block text-sm">{room.label}</span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Step 3: Package Selection */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-8">
                  <span className="text-sm text-[#3D3D3D]">Step 3 of 4</span>
                  <h3 className="text-2xl font-light text-[#2A2A2A] mt-2">Choose your package</h3>
                </div>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  {packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackage(pkg.id)}
                      className={`p-6 border text-left transition-all duration-300 ${
                        selectedPackage === pkg.id
                          ? 'bg-[#B5703A] text-white border-[#B5703A]'
                          : 'bg-[#F5F0E8] text-[#2A2A2A] border-[#C4B6A4] hover:border-[#B5703A]'
                      }`}
                      data-testid={`package-option-${pkg.id}`}
                    >
                      <span className="block text-lg font-medium mb-1">{pkg.name}</span>
                      <span className={`block text-sm mb-3 ${selectedPackage === pkg.id ? 'text-white/80' : 'text-[#4A4A3A]'}`}>
                        {pkg.description}
                      </span>
                      <ul className="space-y-1">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="text-xs flex items-center gap-2">
                            <Check size={12} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 4: Quote & Contact */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-8">
                  <span className="text-sm text-[#3D3D3D]">Step 4 of 4</span>
                  <h3 className="text-2xl font-light text-[#2A2A2A] mt-2">Your Estimate</h3>
                </div>
                
                <div className="bg-[#F5F0E8] p-8 mb-8 text-center">
                  <span className="text-sm text-[#3D3D3D] block mb-2">Estimated Investment</span>
                  <span className="text-4xl lg:text-5xl font-light text-[#2A2A2A]">
                    ₹{estimatedPrice?.toLocaleString('en-IN')}
                  </span>
                  <span className="text-sm text-[#3D3D3D] block mt-2">*Approximate estimate based on selections</span>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="form-input"
                    data-testid="estimate-name-input"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="form-input"
                    data-testid="estimate-phone-input"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="form-input"
                    data-testid="estimate-email-input"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full disabled:opacity-50"
                    data-testid="estimate-submit-btn"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get Detailed Estimate'}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`btn-secondary inline-flex items-center gap-2 ${currentStep === 1 ? 'opacity-0 pointer-events-none' : ''}`}
              data-testid="calculator-back-btn"
            >
              <ArrowLeft size={16} />
              Back
            </button>
            {currentStep < 4 && (
              <button
                onClick={handleNext}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="calculator-next-btn"
              >
                Next
                <ArrowRight size={16} />
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EstimateCalculator;
